'use client';

import { parseAsString, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { MODAL_PARAM } from '@/modals/consts';
import { openVpnDetectedModal, VPN_DETECTED_MODAL } from '@/modals/utils/openVpnDetectedModal';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { shouldOpenVpnModalForUser } from '@/queries/user/user.sockets';
import { useAppSelector } from '@/redux/hooks';

/**
 * Keeps the VPN-detected modal open while the user is flagged. Browser Back,
 * route changes or another modal replacing it all drop the modal param, so
 * reopen it whenever it is not the active modal. Once the user is cleared,
 * drop a modal param left in the URL (e.g. after a reload).
 */
export default function VpnDetectedModalGuard() {
  const userSession = useAppSelector(state => state.user.userSession);
  const { data: user } = useUserMeQuery();
  const [modalParam, setModalParam] = useQueryState(MODAL_PARAM, parseAsString.withDefault(''));
  const isVpnBlocked = Boolean(userSession) && shouldOpenVpnModalForUser(user);
  // A reload keeps the modal param in the URL, so a user cleared meanwhile
  // (e.g. tagged as test) would still see the modal without this.
  const isVpnCleared = Boolean(userSession) && Boolean(user) && !isVpnBlocked;

  useEffect(() => {
    const isVpnModalOpen = modalParam === VPN_DETECTED_MODAL;

    if (isVpnBlocked && !isVpnModalOpen) {
      openVpnDetectedModal();
    } else if (isVpnCleared && isVpnModalOpen) {
      void setModalParam(null);
    }
  }, [isVpnBlocked, isVpnCleared, modalParam, setModalParam]);

  return null;
}
